import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import type { InterludeTypes } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";

/**
 * Link to the Facebook page using the external channel ID.
 */
const FbExternalChannelId: React.FC<{
  externalChannelId: InterludeTypes["Channel"]["externalChannelId"];
}> = ({ externalChannelId }) => {
  return (
    <ExternalLink href={`https://facebook.com/${externalChannelId}`}>
      {externalChannelId}
    </ExternalLink>
  );
};

export const ExternalChannelId: React.FC = () => {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();

  const channelId = useMemo<React.ReactNode>(
    function getDisplayId() {
      const channelType = channel.type;
      switch (channelType) {
        case "line":
          return t("page.channel.config.externalId.line.label", {
            channelId: channel.externalChannelId,
          });
        case "whatsapp":
          return t("page.channel.config.externalId.whatsapp.label", {
            channelId: channel.externalChannelId,
          });
        case "wccs":
          return t("page.channel.config.externalId.wccs.label", {
            channelId: channel.externalChannelId,
          });
        case "fb":
          return (
            <Trans
              i18nKey="page.channel.config.externalId.fb.label"
              components={{
                link: (
                  <FbExternalChannelId
                    externalChannelId={channel.externalChannelId}
                  />
                ),
              }}
            />
          );

        case "ig":
          return t("page.channel.config.externalId.ig.label", {
            channelId: channel.externalChannelId,
          });
        case "email":
          return channel.externalChannelId;
        default:
          channelType satisfies never;
          throw new Error(
            inspectMessage`Unexpected channel type: ${channelType}`,
          );
      }
    },
    [channel.externalChannelId, channel.type, t],
  );

  return <Typography variant="note">{channelId}</Typography>;
};
